import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { DatabaseBackup, Download, CloudUpload } from "lucide-react";

export const BackupCard = () => {
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const params = new URLSearchParams(window.location.search);
  const driveResult = params.get("drive");
  const driveReason = params.get("reason");

  useEffect(() => {
    api.get("/admin/backup/summary").then(({ data }) => setSummary(data)).catch(() => {});
    api.get("/admin/backup/drive-config").then(({ data }) => setCfg(data)).catch(() => {});
    if (driveResult === "connected") toast.success("Google Drive connected");
    if (driveResult === "error") setShowHelp(true);
  }, [driveResult]);

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get("/admin/backup", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vismarks-backup-${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded — store it somewhere safe (your Drive, external disk).");
    } catch {
      toast.error("Backup failed — try again.");
    } finally {
      setBusy(false);
    }
  };

  const pushDrive = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/backup/drive-push");
      toast.success(`Pushed to Google Drive: ${data.file}`);
      api.get("/admin/backup/summary").then(({ data }) => setSummary(data)).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Drive push failed");
    } finally {
      setBusy(false);
    }
  };

  const total = summary ? Object.values(summary.collections).reduce((a, b) => a + b, 0) : 0;
  const daysSince = summary?.last_backup_at
    ? Math.floor((Date.now() - new Date(summary.last_backup_at).getTime()) / 86400000)
    : null;
  const stale = summary && (daysSince === null || daysSince >= 30);

  return (
    <div className="mt-10 rounded-xl border border-[#27272A] bg-[#121212] p-6" data-testid="backup-card">
      <div className="flex items-center gap-2">
        <DatabaseBackup className="h-4 w-4 text-[#007AFF]" />
        <h3 className="font-black text-xl tracking-tight">Data backup</h3>
      </div>
      {stale && (
        <div className="mt-3 rounded-lg border border-[#FF9F0A]/40 bg-[#FF9F0A]/10 px-3 py-2 text-sm text-[#FF9F0A]" data-testid="backup-stale-warning">
          {daysSince === null
            ? "No backup has ever been downloaded — grab your first one now."
            : `Last backup was ${daysSince} days ago — time for a fresh one.`}
        </div>
      )}
      {!stale && summary?.last_backup_at && (
        <p className="mt-2 text-xs text-[#34C759]" data-testid="backup-fresh-note">
          Last backup: {daysSince === 0 ? "today" : `${daysSince} day${daysSince > 1 ? "s" : ""} ago`} ✓
        </p>
      )}
      <p className="mt-2 text-sm text-neutral-400">
        Download a full snapshot of your critical data (users, products, orders, demands, campaigns
        and more) as a ZIP of JSON files — your contingency copy outside this infrastructure.
        {summary && <span className="text-white font-semibold"> {total.toLocaleString()} records across {Object.keys(summary.collections).length} collections.</span>}
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button onClick={download} disabled={busy} data-testid="backup-download-btn"
          className="rounded-full bg-white text-black hover:bg-neutral-200 font-semibold">
          <Download className="h-4 w-4 mr-2" /> {busy ? "Preparing…" : "Download full backup (ZIP)"}
        </Button>
        {summary?.drive_connected ? (
          <Button onClick={pushDrive} disabled={busy} data-testid="backup-drive-push-btn"
            className="rounded-full bg-[#34C759]/15 border border-[#34C759]/40 text-[#34C759] hover:bg-[#34C759]/25 font-semibold">
            <CloudUpload className="h-4 w-4 mr-2" /> Push to Google Drive now
          </Button>
        ) : (
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/oauth/drive/connect`} data-testid="backup-drive-connect-btn">
            <Button className="rounded-full bg-[#007AFF] hover:bg-[#1a86ff] text-white font-semibold">
              <CloudUpload className="h-4 w-4 mr-2" /> Connect Google Drive
            </Button>
          </a>
        )}
      </div>
      {driveResult === "error" && (
        <div className="mt-3 rounded-lg border border-[#FF3B30]/40 bg-[#FF3B30]/10 p-3 text-xs" data-testid="backup-drive-error">
          <div className="font-semibold text-[#FF3B30]">Google Drive didn't connect</div>
          <div className="mt-1 text-neutral-300 font-mono break-all">{driveReason || "unknown error"}</div>
        </div>
      )}
      {!summary?.drive_connected && (
        <button type="button" onClick={() => setShowHelp((v) => !v)} className="mt-2 text-[11px] text-neutral-400 underline underline-offset-4" data-testid="backup-drive-help-toggle">
          {showHelp ? "Hide" : "Show"} Google Cloud setup checklist
        </button>
      )}
      {showHelp && cfg && (
        <div className="mt-2 rounded-lg border border-white/10 bg-black/40 p-3 text-xs text-neutral-300 space-y-1.5" data-testid="backup-drive-help">
          <div>1. Credentials {cfg.configured ? <span className="text-[#34C759]">present ✓</span> : <span className="text-[#FF3B30]">missing — add GOOGLE_DRIVE_CLIENT_ID / SECRET in the secrets panel</span>}{cfg.client_id_hint && <span className="text-neutral-500"> (client {cfg.client_id_hint})</span>}</div>
          <div>2. In Google Cloud Console → APIs &amp; Services → Credentials → your OAuth client → <b>Authorized redirect URIs</b>, add exactly:
            <div className="mt-1 flex items-center gap-2"><code className="bg-black/60 rounded px-2 py-1 break-all text-white" data-testid="backup-drive-redirect-uri">{cfg.redirect_uri}</code>
            <button type="button" onClick={() => { navigator.clipboard.writeText(cfg.redirect_uri); toast.success("Copied"); }} className="h-6 px-2 rounded-full border border-white/20">Copy</button></div></div>
          <div>3. APIs &amp; Services → <b>Library</b> → enable <b>Google Drive API</b>.</div>
          <div>4. <b>OAuth consent screen</b>: if Publishing status is <i>Testing</i>, add your Google account under <b>Test users</b> (or click Publish app). Otherwise Google shows "Access blocked".</div>
          <div>5. Click <b>Connect Google Drive</b> again and approve. If it says <i>no_refresh_token</i>, remove VISMARKS at myaccount.google.com → Security → Third-party access, then connect again.</div>
        </div>
      )}
      {summary?.drive_connected && (
        <p className="mt-2 text-xs text-neutral-500" data-testid="backup-drive-status">
          Google Drive connected ✓ — auto-backup every Sunday 07:00 UTC.
          {summary.last_drive_push && ` Last push: ${new Date(summary.last_drive_push).toLocaleDateString()}.`}
        </p>
      )}
      <p className="mt-3 text-[11px] text-neutral-500">
        Passwords and security secrets are never included. Every ZIP contains a RESTORE-GUIDE.md
        with step-by-step recovery instructions. Weekly auto-push to Google Drive is ready to wire
        up once you add your Google Cloud credentials.
      </p>
    </div>
  );
};
